import { Scan, ZoomIn, ZoomOut } from "lucide-react";
import { useMemo } from "react";

import { Button, Tooltip } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { useDisplaySettingsStore } from "@/settings/store/use-display-settings-store";
import { BoardPatternSurface, BoardSurface } from "@/shelf/components/shelf-board/board-surface";
import { ShelfRack } from "@/shelf/components/shelf-board/shelf-rack";
import { useBoardPan } from "@/shelf/hooks/use-board-pan";
import { useBoardView } from "@/shelf/hooks/use-board-view";
import { selectVisibleShelves } from "@/shelf/utils/board-culling";
import { resolveShelfSpinesBySlotIndex } from "@/shelf/utils/shelf-board-view";

import type { LibraryProject, LibraryShelf } from "@/cloud/types/types";

/**
 * The board every rack stands on, filling whatever it is placed in.
 *
 * It is the whole page rather than a box on it, as the desktop's board is the
 * whole view: a board of racks is a thing to move around in, and a frame
 * partway down a page left the racks a letterbox with the page scrolling around
 * it — and a full-screen button to make up for it.
 *
 * **Board space is millimeters**, and one transform makes one unit one
 * millimeter for everything drawn on it. A rack's width is its capacity times
 * the thickness of a jewel case, so where a slot is, is arithmetic — which is
 * what lets the board decide what to draw without ever measuring the DOM.
 *
 * Drawn in the DOM rather than into a `<canvas>`, and only the racks in front of
 * the viewport are drawn. Every disc is a link to its project, so in the DOM it
 * is an anchor with an accessible name and a focus ring, where a canvas would
 * mean rebuilding all of that by hand.
 */
export const ShelfBoard = ({
  projectsById,
  shelves,
}: {
  projectsById: ReadonlyMap<string, LibraryProject>;
  shelves: readonly LibraryShelf[];
}) => {
  const { t } = useTranslation();
  const boardPattern = useDisplaySettingsStore((state) => state.boardPattern);
  const board = useBoardView(shelves);
  const pan = useBoardPan({
    panBy: board.panBy,
    surfaceRef: board.containerRef,
    zoomAroundBy: board.zoomAroundBy,
  });

  const visible = useMemo(
    () =>
      selectVisibleShelves({
        shelves,
        view: board.view,
        viewportHeightPixels: board.viewport.heightPixels,
        viewportWidthPixels: board.viewport.widthPixels,
      }),
    [board.view, board.viewport.heightPixels, board.viewport.widthPixels, shelves],
  );

  return (
    <div className="relative size-full overflow-hidden bg-background">
      <div
        ref={board.containerRef}
        aria-label={t("shelf.board.label")}
        role="group"
        className="relative size-full cursor-grab touch-none select-none active:cursor-grabbing"
        {...pan}
      >
        {/* The grid gives the racks somewhere to stand and shows that the
            board moves — so it moves with it. */}
        <BoardPatternSurface pattern={boardPattern} view={board.view} />
        <BoardSurface view={board.view}>
          {visible.map((layout) => (
            <ShelfRack
              key={layout.shelf.id}
              layout={layout}
              spinesBySlotIndex={resolveShelfSpinesBySlotIndex({
                projectsById,
                shelf: layout.shelf,
              })}
            />
          ))}
        </BoardSurface>
      </div>

      {/*
        Drawn in screen pixels, outside the surface: everything on the board is
        millimeters, which is right for an object standing on it and wrong for a
        row of buttons — at either end of the zoom range those would be a smear
        across the window or three dots too small to hit.
      */}
      <div className="absolute bottom-4 right-4 flex items-center gap-1 rounded-md border border-border bg-card p-1 shadow-raised">
        <Tooltip content={t("shelf.zoomOut")} side="top">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={board.zoomOut}
            disabled={!board.canZoomOut}
            aria-label={t("shelf.zoomOut")}
          >
            <ZoomOut aria-hidden />
          </Button>
        </Tooltip>
        {/* A reading rather than a control, so it is not a button. Tabular so
            the two buttons beside it do not shuffle as the number changes. */}
        <span className="min-w-14 text-center text-xs tabular-nums text-muted-foreground">
          {t("shelf.zoomLevel", { percentage: board.zoomPercentage })}
        </span>
        <Tooltip content={t("shelf.zoomIn")} side="top">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={board.zoomIn}
            disabled={!board.canZoomIn}
            aria-label={t("shelf.zoomIn")}
          >
            <ZoomIn aria-hidden />
          </Button>
        </Tooltip>
        <Tooltip content={t("shelf.fit")} side="top">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={board.fit}
            aria-label={t("shelf.fit")}
          >
            <Scan aria-hidden />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
